import axios from 'axios';
import React, { useEffect, useState } from 'react';
import { Button, Modal } from 'semantic-ui-react';
import styled from '@emotion/styled';
import moment from 'moment';
import { FeedbackEntry } from '.';

const DocList = styled.div`
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
`;

const DocLink = styled.a`
  color: #ff5252;
  font-size: 13px;
  text-decoration: underline;
  cursor: pointer;
`;

const JsonPanel = styled.pre`
  background: #f5f5f5;
  border-radius: 4px;
  padding: 12px;
  font-size: 11px;
  max-height: 420px;
  overflow: auto;
  white-space: pre-wrap;
  word-break: break-word;
  flex: 1;
  min-width: 0;
`;

const JsonRow = styled.div`
  display: flex;
  gap: 16px;
  margin-top: 12px;
`;

const JsonLabel = styled.div`
  font-weight: bold;
  font-size: 13px;
  margin-bottom: 6px;
  color: #555;
`;

const NotesSection = styled.div`
  margin-top: 20px;
  border-top: 1px solid #eee;
  padding-top: 16px;
`;

const NotesTextarea = styled.textarea`
  width: 100%;
  border: 1px solid #ddd;
  border-radius: 4px;
  padding: 8px 10px;
  font-size: 13px;
  font-family: Lato, sans-serif;
  resize: vertical;
  min-height: 80px;
  color: #333;
  box-sizing: border-box;
`;

const statusOptions = [
  { key: 'pending', text: 'Pending Review', value: 'pending' },
  { key: 'mismatch', text: 'Mismatch', value: 'mismatch' },
  { key: 'match', text: 'Match', value: 'match' },
];

const statusColor = (s: string | null) => {
  if (s === 'Match') return 'green';
  if (s === 'Mismatch') return 'red';
  return 'grey';
};

interface IProps {
  selected: FeedbackEntry | null;
  setSelected: React.Dispatch<React.SetStateAction<FeedbackEntry>>;
  entries: FeedbackEntry[];
  setEntries: React.Dispatch<React.SetStateAction<FeedbackEntry[]>>;
  savingId: number | null;
  setSavingId: (id: number | null) => void;
  submitReview: (id: number, status: 'Match' | 'Mismatch') => void;
}

export default function ReviewModal({
  selected,
  setSelected,
  entries,
  setEntries,
  savingId,
  setSavingId,
  submitReview,
}: IProps) {
  const [extractionHtml, setExtractionHtml] = useState('');
  const [actualHtml, setActualHtml] = useState('');
  const [notesDraft, setNotesDraft] = useState('');
  const [savingNotes, setSavingNotes] = useState(false);

  const saveNotes = async () => {
    if (!selected) return;
    setSavingNotes(true);
    try {
      await axios.post('/api/orders/SetTaxExtractionFeedbackNotes', { id: selected.id, notes: notesDraft });
      setEntries(prev => prev.map(e => (e.id === selected.id ? { ...e, notes: notesDraft } : e)));
      setSelected(prev => (prev ? { ...prev, notes: notesDraft } : prev));
    } finally {
      setSavingNotes(false);
    }
  };
  const getHtml = async (entry: string) => {
    const html = await axios.post('/api/searches/CreatePDF', {
      template: 'taxSearch',
      data: {buildingClass: {}, searchTypeId: 20,propertyId: selected?.propertyId, ...JSON.parse(entry)},
    });
    return html;
  };

  const parseDocIds = (json: string): number[] => {
    try {
      return JSON.parse(json) as number[];
    } catch {
      return [];
    }
  };

  useEffect(() => {
    getHtml(selected.aiExtractionJson).then(html => setExtractionHtml(html.data));
    getHtml(selected.finalFormJson).then(html => setActualHtml(html.data));
    setNotesDraft(selected?.notes ?? '');
  }, [selected?.id]);

  return (
    <Modal open={!!selected} onClose={() => setSelected(null)} size="fullscreen">
      <Modal.Header>
        Feedback #{selected?.id} — Property {selected?.propertyId}
        {selected && (
          <div style={{ fontSize: 13, fontWeight: 'normal', marginTop: 4, color: '#666' }}>
            {moment(selected.createdAt).format('MMMM D, YYYY h:mm a')}
            {' · '}
            {selected.municipalityName || `ID ${selected.municipalityId}`}
            {selected.municipalityType ? ` (${selected.municipalityType})` : ''}
            {selected.subMunicipalityName ? ` / ${selected.subMunicipalityName}` : ''}
          </div>
        )}
      </Modal.Header>
      <Modal.Content scrolling>
        {selected && (
          <>
            <div style={{ marginBottom: 16 }}>
              <strong style={{ fontSize: 13 }}>Documents used:</strong>
              <DocList style={{ marginTop: 6 }}>
                {parseDocIds(selected.documentIds).map((docId, i) => (
                  <DocLink
                    key={docId}
                    href={`/api/documents/GetDocument/${docId}`}
                    target="_blank"
                    rel="noreferrer"
                  >
                    Open Doc {i + 1} (ID {docId})
                  </DocLink>
                ))}
              </DocList>
            </div>
            <JsonRow>
              <div style={{ flex: 1, minWidth: 0 }}>
                <JsonLabel>AI Extraction Result</JsonLabel>
                <JsonPanel>
                  <iframe
                    style={{ width: '100%', height: 1000, overflowX: 'hidden' }}
                    srcDoc={extractionHtml}
                  />
                </JsonPanel>
              </div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <JsonLabel>What User Saved</JsonLabel>
                <JsonPanel>
                  <iframe style={{ width: '100%', height: 1000, overflowX: 'hidden' }} srcDoc={actualHtml} />
                </JsonPanel>
              </div>
            </JsonRow>

            <NotesSection>
              <JsonLabel>Notes on discrepancy</JsonLabel>
              <NotesTextarea
                placeholder="Describe what was wrong or what the AI should have extracted differently…"
                value={notesDraft}
                onChange={e => setNotesDraft(e.target.value)}
              />
              <div style={{ marginTop: 8 }}>
                <Button
                  size="small"
                  color="blue"
                  loading={savingNotes}
                  disabled={savingNotes || notesDraft === (selected.notes ?? '')}
                  onClick={saveNotes}
                >
                  Save Notes
                </Button>
                {notesDraft !== (selected.notes ?? '') && (
                  <span style={{ fontSize: 12, color: '#999', marginLeft: 10 }}>Unsaved changes</span>
                )}
              </div>
            </NotesSection>
          </>
        )}
      </Modal.Content>
      <Modal.Actions>
        {selected && !selected.reviewStatus && (
          <>
            <Button
              color="green"
              loading={savingId === selected.id}
              disabled={savingId === selected.id}
              onClick={() => submitReview(selected.id, 'Match')}
            >
              Mark as Match
            </Button>
            <Button
              color="red"
              loading={savingId === selected.id}
              disabled={savingId === selected.id}
              onClick={() => submitReview(selected.id, 'Mismatch')}
            >
              Mark as Mismatch
            </Button>
          </>
        )}
        <Button onClick={() => setSelected(null)}>Close</Button>
      </Modal.Actions>
    </Modal>
  );
}
